/* eslint-disable no-await-in-loop */
/* eslint-disable no-bitwise */
import { toast } from 'react-toastify';
import domains from '../containers/Domains';

const { featureFlags } = domains || {};
const { localFBCaching } = featureFlags || {};
const { localFBCachingExpiration } = featureFlags || 300000;

export const clearFbCacheInLocalStorage = () => {
  if (localFBCaching) {
    const localStorageItemKeys = Object.keys(window.localStorage);
    localStorageItemKeys.forEach((key) => {
      if (key.includes('fbcache-')) {
        window.localStorage.removeItem(key);
      }
    });
  }
};

// todo:
// JustFetch
// ref: linme 64 const jsonResult = await result.json();
// check headers for rate limits

export const getLocalStorageDataOrFetch = async (
  queryWithParameters,
  skipCache
) => {
  let jsonResult = {};
  const queryHashCode = hashCode(queryWithParameters);
  const queryHashCodeDataName = `fbcache-data-${queryHashCode}`;
  const queryHashCodeExpiryName = `fbcache-expiry-${queryHashCode}`;

  const localStorageData = window.localStorage.getItem(queryHashCodeDataName);
  const localStorageDataExpiry = window.localStorage.getItem(
    queryHashCodeExpiryName
  );
  const currentTimeAndDate = Date.now();

  // AV2-3695: fix for client_pages data being too large
  const enableCaching =
    localFBCaching ||
    queryWithParameters.includes('_pages') ||
    queryWithParameters.includes('me/accounts');

  if (
    (enableCaching &&
      localStorageData &&
      localStorageDataExpiry > currentTimeAndDate) ||
    skipCache === false
  ) {
    jsonResult = JSON.parse(localStorageData);
  } else {
    // stores fetches AND stores updated data in cache
    jsonResult = await setLocalStorageData(
      queryWithParameters,
      queryHashCodeDataName,
      queryHashCodeExpiryName,
      currentTimeAndDate
    );
  }
  return jsonResult;
};

const delay = ms => new Promise(res => setTimeout(res, ms));

export const setLocalStorageData = async (
  queryWithParameters,
  queryHashCodeDataName,
  queryHashCodeExpiryName,
  currentTimeAndDate
) => {
  const expiryTime = currentTimeAndDate
    ? currentTimeAndDate + localFBCachingExpiration
    : Date.now() + localFBCachingExpiration;

  const isPagesService =
    queryWithParameters.includes('_pages') ||
    queryWithParameters.includes('me/accounts');

  let jsonResult = { data: [] };

  if (!isPagesService) {
    const result = await fetch(queryWithParameters);
    jsonResult = await result.json();
  } else {
    // AV2-3695: fix for client_pages data being too large
    let currentPages = [];
    console.log('initial call - pagesResult status', queryWithParameters.split("?")[0]);
    const pagesResult = await fetch(queryWithParameters);
    const pagesJsonResult = await pagesResult.json();    

    const totalRetryCount = 30;
    let retryCount = 0;

    if (pagesJsonResult.error) {
      jsonResult.error = pagesJsonResult.error;
    } else {
      currentPages = currentPages.concat(pagesJsonResult.data);
      let afterToken = pagesJsonResult.paging.cursors.after;      
      while (afterToken) {
        const currResult = await fetch(
          `${queryWithParameters}&after=${afterToken}`
        );

        // console.log('after - currResult status', currResult.status);
        const currentJsonResult = await currResult.json();
        if (currResult.status !== 200) {
          console.log('fb request error', currResult.status, currentJsonResult.error);
          jsonResult.error = currentJsonResult.error;         

          if (retryCount > totalRetryCount) {
            console.log('max retry reached', queryWithParameters.split("?")[0]);
            afterToken = null;
            break;
          }
          else {            
            await delay(10000);
            retryCount += 1;
            console.log('retrying -', queryWithParameters.split("?")[0], ' - attempt - ', retryCount);
          }           
        }
        else {
          currentPages = currentPages.concat(currentJsonResult.data);
          if (currentJsonResult.data.length === 0) {
            afterToken = null;
          }
          else {
            afterToken = currentJsonResult.paging.cursors.after;
          }          
        }        
      }
    }

    jsonResult.data = currentPages;
  }

  const { error } = jsonResult;
  if (error) {
    const { message: errorMessage } = error;
    if (
      errorMessage.includes('Application request limit reached') ||
      errorMessage.includes('There have been too many calls to this ad-account')
    ) {
      toast(
        'API Rate Limit has been reached. Please be patient and take a break :)',
        {
          position: toast.POSITION.BOTTOM_CENTER,
          autoClose: 3000,
          type: toast.TYPE.ERROR,
          hideProgressBar: true
        }
      );
    }
  }

  if (localFBCaching || isPagesService) {
    try {
      const isLocalStorageFull = checkIfLocalStorageIsFull();
      if (isLocalStorageFull) {
        throw new Error('local storage is full');
      }
      setLocalStorageItems(
        queryHashCodeDataName,
        queryHashCodeExpiryName,
        jsonResult,
        expiryTime
      );
    } catch (e) {
      clearHalfOfLocalStorage();
      setLocalStorageItems(
        queryHashCodeDataName,
        queryHashCodeExpiryName,
        jsonResult,
        expiryTime
      );
    }
  }
  return jsonResult;
};

const setLocalStorageItems = (
  queryHashCodeDataName,
  queryHashCodeExpiryName,
  jsonResult,
  expiryTime
) => {
  window.localStorage.setItem(
    queryHashCodeDataName,
    JSON.stringify(jsonResult)
  );
  window.localStorage.setItem(queryHashCodeExpiryName, expiryTime);
};

const checkIfLocalStorageIsFull = () => {
  window.navigator.webkitTemporaryStorage.queryUsageAndQuota(
    (usedSpace, remainingSpace) => {
      const totalStorage = usedSpace + remainingSpace;
      const storageLimit = totalStorage * 0.9;
      const isLocalStorageFull = usedSpace >= storageLimit;
      return isLocalStorageFull;
    },
    (error) => {
      console.log('An error occurred in checkIfLocalStorageIsFull(): ', error);
      return true;
    }
  );
};

const clearHalfOfLocalStorage = () => {
  const localStorageItemKeys = Object.keys(window.localStorage);
  const expiryItemsArray = [];
  localStorageItemKeys.forEach((key) => {
    if (key.includes('fbcache-expiry')) {
      expiryItemsArray.push({
        key,
        expiry: parseInt(localStorage.getItem(key), 10)
      });
    }
  });
  const sortedExpiryItems = expiryItemsArray.sort(
    (a, b) => a.expiry - b.expiry
  );
  const sortedExpiryItemsLength = sortedExpiryItems.length / 2;
  for (let i = 0; i < sortedExpiryItemsLength; i += 1) {
    const expiryItemKey = sortedExpiryItems[i].key;
    const hashCode = extractHashFromKey(expiryItemKey);
    window.localStorage.removeItem(`fbcache-expiry-${hashCode}`);
    window.localStorage.removeItem(`fbcache-data-${hashCode}`);
  }
};

const hashCode = function(inputString) {
  let hash = 0;
  let i;
  let chr;
  if (inputString.length === 0) return hash;
  for (i = 0; i < inputString.length; i += 1) {
    chr = inputString.charCodeAt(i);
    hash = (hash << 5) - hash + chr;
    hash |= 0; // Convert to 32bit integer
  }
  return hash;
};

const extractHashFromKey = (key) => {
  let hashFromKey = '';
  const splitKeyString = key.split('-');
  // all fb-cache item names follow the same pattern
  // the hashCode an be (-) so there mya be an extra -
  // so split may yield 4 elements
  if (splitKeyString.length === 4) {
    hashFromKey = `-${splitKeyString[3]}`;
  } else {
    hashFromKey = splitKeyString[2];
  }
  return hashFromKey;
};



// WEBPACK FOOTER //
// ./src/fbApi/fbCallManager.js